"use client"

import { useState, useEffect } from "react"
import { Badge, Button, Alert, Spinner, Dropdown, Modal } from "react-bootstrap"
import { toast } from "react-hot-toast"
import { apiRequest } from "../utils/api"

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken")
    return {
      Authorization: `Bearer ${token}`,
    }
  }

  // ✅ fetch logic inside useEffect
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true)
        const response = await apiRequest("/api/admin/notifications", {
          headers: getAuthHeaders(),
        })
        setNotifications(response.notifications || [])
        setUnreadCount(response.unreadCount || 0)
      } catch (error) {
        console.error("Error fetching notifications:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchNotifications()

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  const markAsRead = async (notificationId) => {
    try {
      await apiRequest(`/api/admin/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: getAuthHeaders(),
      })

      setNotifications((prev) =>
        prev.map((notif) => (notif.id === notificationId ? { ...notif, read: true } : notif)),
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      toast.error("Failed to mark notification as read")
    }
  }

  const markAllAsRead = async () => {
    try {
      await apiRequest("/api/admin/notifications/read-all", {
        method: "PUT",
        headers: getAuthHeaders(),
      })

      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })))
      setUnreadCount(0)
      toast.success("All notifications marked as read")
    } catch (error) {
      toast.error("Failed to mark all notifications as read")
    }
  }

  const deleteNotification = async (notificationId) => {
    try {
      await apiRequest(`/api/admin/notifications/${notificationId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      })

      setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId))
      if (!notifications.find((n) => n.id === notificationId)?.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1))
      }
      toast.success("Notification deleted")
    } catch (error) {
      toast.error("Failed to delete notification")
    }
  }

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case "critical":
        return "danger"
      case "warning":
        return "warning"
      case "info":
        return "info"
      default:
        return "secondary"
    }
  }

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return "🚨"
      case "warning":
        return "⚠️"
      case "info":
        return "ℹ️"
      default:
        return "📢"
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case "temperature_alert":
        return "🌡️"
      case "crop_warning":
        return "🌾"
      case "system_alert":
        return "⚙️"
      default:
        return "📢"
    }
  }

  const formatTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))

    if (diffInMinutes < 1) return "Just now"
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return `${Math.floor(diffInMinutes / 1440)}d ago`
  }

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle variant="outline-primary" className="position-relative">
          🔔 Notifications
          {unreadCount > 0 && (
            <Badge
              bg="danger"
              className="position-absolute top-0 start-100 translate-middle rounded-pill"
              style={{ fontSize: "0.6rem" }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu style={{ width: "400px", maxHeight: "500px", overflowY: "auto" }}>
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <h6 className="mb-0">Notifications</h6>
            {unreadCount > 0 && (
              <Button size="sm" variant="link" onClick={markAllAsRead}>
                Mark all read
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-3">
              <Spinner size="sm" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <div className="mb-2">📭</div>
              <small>No notifications</small>
            </div>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <div
                key={notification.id}
                className={`px-3 py-2 border-bottom ${!notification.read ? "bg-light" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedNotification(notification)
                  setShowModal(true)
                  if (!notification.read) {
                    markAsRead(notification.id)
                  }
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="me-2">{getTypeIcon(notification.type)}</div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="mb-1 small fw-bold">
                        {notification.title}
                        {!notification.read && (
                          <Badge bg="primary" className="ms-2" style={{ fontSize: "0.6rem" }}>
                            New
                          </Badge>
                        )}
                      </h6>
                      <Badge bg={getSeverityVariant(notification.severity)} className="ms-2">
                        {getSeverityIcon(notification.severity)}
                      </Badge>
                    </div>
                    <p className="mb-1 small text-muted" style={{ fontSize: "0.8rem" }}>
                      {notification.message.length > 80
                        ? `${notification.message.substring(0, 80)}...`
                        : notification.message}
                    </p>
                    <small className="text-muted">{formatTimeAgo(notification.createdAt)}</small>
                  </div>
                </div>
              </div>
            ))
          )}

          {notifications.length > 10 && (
            <div className="text-center py-2">
              <Button size="sm" variant="link">
                View all notifications
              </Button>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* Notification Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            {selectedNotification && (
              <>
                {getTypeIcon(selectedNotification.type)}
                <span className="ms-2">{selectedNotification.title}</span>
                <Badge bg={getSeverityVariant(selectedNotification.severity)} className="ms-2">
                  {selectedNotification.severity}
                </Badge>
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNotification && (
            <>
              <Alert variant={getSeverityVariant(selectedNotification.severity)}>{selectedNotification.message}</Alert>

              {selectedNotification.cropName && (
                <div className="mb-3">
                  <strong>Crop:</strong> {selectedNotification.cropName}
                </div>
              )}

              {selectedNotification.temperature && (
                <div className="mb-3">
                  <strong>Current Temperature:</strong> {selectedNotification.temperature}°C
                  {selectedNotification.optimalRange && (
                    <div className="text-muted small">
                      Optimal range: {selectedNotification.optimalRange.min}°C - {selectedNotification.optimalRange.max}
                      °C
                    </div>
                  )}
                </div>
              )}

              {selectedNotification.location && (
                <div className="mb-3">
                  <strong>Location:</strong>{" "}
                  {selectedNotification.location.name ||
                    `${selectedNotification.location.lat}, ${selectedNotification.location.lng}`}
                </div>
              )}

              <div className="text-muted small">
                <strong>Created:</strong> {new Date(selectedNotification.createdAt).toLocaleString()}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={() => {
              if (selectedNotification) {
                deleteNotification(selectedNotification.id)
                setShowModal(false)
              }
            }}
          >
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default NotificationCenter
